import {
  BarChart3,
  CalendarDays,
  CheckSquare,
  FilePlus2,
  Home,
  LayoutDashboard,
  LineChart,
  type LucideIcon,
  Settings,
  Users,
} from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { navigationFor } from '../session/navigation.js';
import { useLogout, useMe } from '../session/session.js';
import { ProblemMessage } from './problem-message.js';

/** Icono de cada destino de `navigationFor`, como en la Power App (`src/components/app-shell.tsx`). */
const ICONS: Readonly<Record<string, LucideIcon>> = {
  '/': Home,
  '/solicitudes/nueva': FilePlus2,
  '/mis-solicitudes': CalendarDays,
  '/mi-equipo': Users,
  '/panel-responsable': LayoutDashboard,
  '/aprobaciones': CheckSquare,
  '/analitica': LineChart,
  '/administracion': Settings,
};

/**
 * Estructura de las pantallas autenticadas, con el aspecto de la Power App: barra lateral fija (desde `lg`)
 * con la marca, la navegación según `GET /me` (W1) y la ficha del usuario con el cierre de sesión; en
 * pantallas estrechas, una franja superior con la marca y el cierre de sesión, y la navegación abajo.
 * La navegación no protege nada: lo decide la API en cada pantalla.
 */
export function AppShell() {
  const me = useMe();
  const { logout, pending, error } = useLogout();
  const items = navigationFor(me);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-sidebar-border bg-sidebar text-sidebar-foreground lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:flex-col lg:items-stretch lg:justify-start lg:gap-0 lg:border-r lg:border-b-0">
        <div className="px-4 py-3 lg:border-b lg:border-sidebar-border lg:px-5 lg:py-5">
          <div className="flex items-center gap-3">
            <div className="grid size-9 place-items-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <BarChart3 aria-hidden className="size-4" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold tracking-tight">Permisos</p>
              <p className="truncate text-xs text-sidebar-foreground/70">Gestión interna</p>
            </div>
          </div>
        </div>
        <nav aria-label="Principal" className="hidden flex-1 overflow-y-auto p-3 lg:block">
          <ul className="space-y-1">
            {items.map((item) => {
              const Icon = ICONS[item.to];
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                          : 'text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive ? (
                          <span aria-hidden className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-sidebar-primary-foreground/90" />
                        ) : null}
                        {Icon ? <Icon aria-hidden className="size-4 shrink-0" /> : null}
                        <span className="truncate">{item.label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="flex min-w-0 items-center gap-3 px-4 py-2 lg:block lg:border-t lg:border-sidebar-border lg:p-4">
          <div className="hidden min-w-0 sm:block">
            <p className="truncate text-sm font-medium">{me.fullName}</p>
            <p className="mt-0.5 truncate text-xs text-sidebar-foreground/65">{me.email}</p>
          </div>
          <button
            type="button"
            onClick={logout}
            disabled={pending}
            className="shrink-0 rounded-md border border-sidebar-border px-3 py-1.5 text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground disabled:opacity-50 lg:mt-3 lg:w-full"
          >
            Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="pb-24 lg:ml-64 lg:pb-0">
        <div className="mx-auto max-w-5xl px-4 py-6">
          {error !== undefined ? <ProblemMessage error={error} /> : null}
          <Outlet />
        </div>
      </main>
      <nav
        aria-label="Navegación móvil"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card px-1 py-1.5 lg:hidden"
      >
        <ul className="flex items-center gap-1 overflow-x-auto">
          {items.map((item) => {
            const Icon = ICONS[item.to];
            return (
              <li key={item.to} className="flex-1 shrink-0">
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `flex min-w-16 flex-col items-center gap-1 rounded-md px-2 py-1.5 text-[10px] font-medium whitespace-nowrap transition-colors ${
                      isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`
                  }
                >
                  {Icon ? <Icon aria-hidden className="size-5" /> : null}
                  <span>{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
